import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { init, send } from 'emailjs-com';
import About from '../components/About';
import Services from '../components/Services';
import FAQs from '../components/FAQs';
import {
  trackFormStart,
  trackFormFieldFocus,
  trackFormSubmit,
  trackFormAbandonment,
  trackPageView
} from '../utils/tracking';
import {
  FaRegHandshake,
  FaCompassDrafting,
  FaRegFileLines,
  FaFileSignature,
  FaHelmetSafety,
  FaBolt,
  FaLifeRing
} from 'react-icons/fa6';
import { TbHome, TbBriefcase } from 'react-icons/tb';

// Initialize EmailJS
init("d1zH6UNPIGfFtdCEu");

function Main() {
  const formStartTime = useRef(Date.now());
  const [formData, setFormData] = useState({
    category: 'residential',
    name: '',
    phone: '',
    email: '',
    pincode: '',
    city: '',
    monthlyBill: '',
    companyName: ''
  });
  
  useEffect(() => {
    trackPageView('/', 'Home Page');
  }, []);



  // Disable browser's automatic scroll restoration to use our custom logic
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Save scroll position continuously while on home page
  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      // Debounce scroll position saving
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        sessionStorage.setItem('homeScrollPosition', window.pageYOffset.toString());
      }, 100);
    };

    // Save scroll position on scroll
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also save on beforeunload (when navigating away)
    const handleBeforeUnload = () => {
      sessionStorage.setItem('homeScrollPosition', window.pageYOffset.toString());
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Save initial scroll position if not already saved
    if (!sessionStorage.getItem('homeScrollPosition')) {
      sessionStorage.setItem('homeScrollPosition', window.pageYOffset.toString());
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearTimeout(scrollTimeout);
      // Save final scroll position when component unmounts
      sessionStorage.setItem('homeScrollPosition', window.pageYOffset.toString());
    };
  }, []);

  useEffect(() => {
    // Add FAQ structured data for homepage
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.id = 'homepage-faq-schema';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does solar panel installation cost in India?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar panel installation in India costs ₹45,000-70,000 per kW. A 3 kW residential system costs approximately ₹1.8-2.4 lakhs, while a 5 kW system costs ₹3-4 lakhs. With PM Surya Ghar subsidy (up to ₹78,000 for 3+ kW systems), the effective cost reduces significantly. Commercial systems typically cost ₹35-50 per watt."
          }
        },
        {
          "@type": "Question",
          "name": "What is the solar subsidy in India?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The PM Surya Ghar subsidy in India provides up to ₹78,000 for residential solar systems. For systems up to 1 kW: ₹30,000. For 1-2 kW: ₹30,000 for first kW + ₹18,000 per additional kW. For systems above 3 kW (up to 10 kW): Maximum ₹78,000. Commercial installations can benefit from accelerated depreciation of 40-80% in the first year."
          }
        },
        {
          "@type": "Question",
          "name": "How much can I save with solar panels?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar panels typically reduce electricity bills by 70-90%. A 3 kW system can save ₹2,000-4,000 monthly, while a 5 kW system saves ₹4,000-8,000 monthly. Over 25 years, total savings typically range from ₹6-24 lakhs for residential systems. Commercial systems can save ₹5-50 lakhs annually depending on system size."
          }
        },
        {
          "@type": "Question",
          "name": "How long does solar panel installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar panel installation typically takes 1-2 days for residential systems. However, the complete process from application to activation takes 1-2 months: net metering application (20-30 days), approval (5-10 days), installation (1-2 days), DISCOM inspection (5-7 days), and grid connection (3-5 days). Commercial installations may take longer."
          }
        },
        {
          "@type": "Question",
          "name": "What is the payback period for solar panels?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar panel payback period is typically 4-6 years for residential systems and 3-5 years for commercial systems (with accelerated depreciation benefits). After payback, you get nearly free electricity for 20+ years. The system provides excellent ROI, typically 15-30% annually."
          }
        },
        {
          "@type": "Question",
          "name": "Do solar panels require maintenance?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar panels require minimal maintenance. They need periodic cleaning (every 6-12 months) and occasional inspection. No moving parts mean fewer breakdowns. Most systems come with 25-year warranties. Lunar Electric provides 1 year of free maintenance including quarterly cleaning and health checks."
          }
        },
        {
          "@type": "Question",
          "name": "What is net metering?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Net metering allows you to sell excess solar electricity back to the grid. When your solar system produces more power than you use, the excess flows to the grid and your meter runs backward. At the end of the billing cycle, you only pay for net consumption (units consumed minus units exported), making rooftop solar financially attractive."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need permission to install solar panels?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, you need net metering approval from your DISCOM (electricity distribution company). The process involves application submission, feasibility assessment, and approval. For residential installations on your own property, you typically don't need separate building permission, but DISCOM approval is mandatory. Lunar Electric helps with all paperwork and approvals."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    // Check if we have a saved scroll position (user coming back from another page)
    const savedScrollPosition = sessionStorage.getItem('homeScrollPosition');
    
    // Function to restore scroll position with multiple attempts to handle layout shifts
    const restoreScrollPosition = (scrollPos, retries = 3) => {
      if (scrollPos <= 0) return;
      
      const restore = () => {
        // Use instant scroll to avoid smooth scroll offset issues
        window.scrollTo(0, scrollPos);
        
        // Verify the scroll position was set correctly after a frame
        requestAnimationFrame(() => {
          const currentScroll = window.pageYOffset;
          // If there's a difference, try again (layout might still be shifting)
          if (Math.abs(currentScroll - scrollPos) > 5 && retries > 0) {
            setTimeout(() => restoreScrollPosition(scrollPos, retries - 1), 50);
          }
        });
      };
      
      restore();
    };
    
    // Handle hash scrolling when coming from other pages or callback page
    const handleHashScroll = () => {
      const hash = window.location.hash;
      if (hash) {
        setTimeout(() => {
          // Try multiple selectors for better compatibility
          const element = document.querySelector(hash) || 
                         document.getElementById(hash.substring(1)) ||
                         document.querySelector(`.${hash.substring(1)}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300); // Increased delay to ensure page is fully rendered
      }
    };

    // Wait for page to be fully loaded and rendered before restoring scroll
    const restoreAfterLoad = () => {
      if (window.location.hash) {
        handleHashScroll();
      } else if (savedScrollPosition) {
        const scrollPosition = parseInt(savedScrollPosition, 10);
        if (scrollPosition > 0) {
          // Wait for images and layout to settle
          if (document.readyState === 'complete') {
            // Page already loaded, restore immediately but with verification
            requestAnimationFrame(() => {
              restoreScrollPosition(scrollPosition);
            });
          } else {
            // Wait for page to fully load
            window.addEventListener('load', () => {
              requestAnimationFrame(() => {
                restoreScrollPosition(scrollPosition);
              });
            }, { once: true });
          }
        }
      } else {
        // Only scroll to top on fresh page load (first visit or refresh)
        const navigationEntry = performance.getEntriesByType('navigation')[0];
        if (navigationEntry && navigationEntry.type === 'navigate') {
          window.scrollTo(0, 0);
        }
      }
    };

    // Check if page is already loaded
    if (document.readyState === 'complete') {
      restoreAfterLoad();
    } else {
      // Wait for DOM to be ready, then restore
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', restoreAfterLoad, { once: true });
      } else {
        // DOMContentLoaded already fired, but page might not be fully loaded
        restoreAfterLoad();
      }
    }

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashScroll);

    // Listen for browser back/forward buttons
    const handlePopState = () => {
      const savedPos = sessionStorage.getItem('homeScrollPosition');
      if (savedPos) {
        requestAnimationFrame(() => {
          const scrollPosition = parseInt(savedPos, 10);
          if (scrollPosition > 0) {
            restoreScrollPosition(scrollPosition);
          }
        });
      }
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('hashchange', handleHashScroll);
      window.removeEventListener('popstate', handlePopState);
      // Cleanup FAQ script
      const faqScriptElement = document.getElementById('homepage-faq-schema');
      if (faqScriptElement) {
        faqScriptElement.remove();
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleFieldFocus = (fieldName) => {
    trackFormFieldFocus('main-contact-form', fieldName);
  };
  
  // Track form start when form is visible/rendered
  useEffect(() => {
    const formElement = document.querySelector('.quick-contact-form');
    if (formElement) {
      trackFormStart('main-contact-form', 'Home Page Contact Form');
    }
  }, []);
  
  // Track abandonment on page unload
  useEffect(() => {
    return () => {
      const fieldsFilled = Object.values(formData).filter(v => v && v !== 'residential').length;
      if (fieldsFilled > 0 && fieldsFilled < Object.keys(formData).length - 1) {
        const timeSpent = Math.floor((Date.now() - formStartTime.current) / 1000);
        trackFormAbandonment('main-contact-form', 'Home Page Contact Form', fieldsFilled, timeSpent);
      }
    };
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const emailData = {
      name: formData.name,
      email: formData.email || 'Not provided',
      subject: formData.phone,
      message: `LANDING PAGE CONTACT FORM DETAILS:
Category: ${formData.category}
Phone: ${formData.phone}
Email: ${formData.email || 'Not provided'}
Pincode: ${formData.pincode || 'Not provided'}
City: ${formData.city || 'Not provided'}
Monthly Bill: ${formData.monthlyBill || 'Not provided'}
Company Name: ${formData.companyName || 'Not provided'}

This is a contact request from the Lunar Electric landing page.`
    };

    send('service_9uk4fgz', 'template_uuwsowl', emailData)
      .then((response) => {
        console.log('Contact request received', response.status, response.text);
        
        // Track successful form submission
        const fieldsFilled = Object.values(formData).filter(v => v && v !== 'residential').length;
        trackFormSubmit('main-contact-form', 'Home Page Contact Form', true, fieldsFilled);
        
        setFormData({
          category: 'residential',
          name: '',
          phone: '',
          email: '',
          pincode: '',
          city: '',
          monthlyBill: '',
          companyName: ''
        });
        alert('Thank you! We will contact you soon.');
      })
      .catch((err) => {
        console.error('Failed to send contact request', err);
        
        // Track failed form submission
        const fieldsFilled = Object.values(formData).filter(v => v && v !== 'residential').length;
        trackFormSubmit('main-contact-form', 'Home Page Contact Form', false, fieldsFilled);
        
        alert('Failed to send request, please try again.');
      });
  };

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-to-main" style={{position: 'absolute', left: '-9999px', zIndex: 9999}} onFocus={(e) => e.target.style.left = '0'}>Skip to main content</a>
      <main id="main-content">
        <Services />
        
        {/* Why Choose Us CTA Banner */}
        <section className="why-choose-cta-banner">
          <div className="container">
            <div className="why-choose-cta-banner-content">
              <div className="why-choose-cta-banner-text">
                <h2 className="why-choose-cta-banner-title">Why Choose Lunar Electric?</h2>
                <p className="why-choose-cta-banner-subtitle">Storm-Proof & Rust-Proof Solar Structures Built to Last Decades</p>
              </div>
              <Link to="/why-choose-us" className="btn btn-primary why-choose-cta-banner-btn">
                Learn More
              </Link>
            </div>
          </div>
        </section>
        
        {/* Quick Contact Form Section */}
        <section className="quick-contact-section">
          <div className="container">
            <div className="quick-contact-wrapper">
              <div className="quick-contact-content">
                <h2 className="quick-contact-title">Go Solar Today</h2>
                <p className="quick-contact-subtitle">Get your free solar consultation - residential or commercial</p>
                <div className="quick-contact-benefits">
                  <div className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span>Free consultation at your preferred time</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span>Expert advice from our professionally trained engineers</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span>Super affordable custom solutions</span>
                  </div>
                </div>
              </div>
              
              <div className="quick-contact-form-wrapper">
                <div className="callback-form-wrapper-compact">
                  <h3 className="form-title-compact">Get a Free Quote</h3>
                  
                  {/* Category Selection */}
                  <div className="category-selection-compact">
                    <div className="category-options-compact">
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-residential-landing"
                          name="category"
                          value="residential"
                          checked={formData.category === 'residential'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-residential-landing" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbHome size={20} />
                          </span>
                          <span className="category-text">Residential</span>
                        </label>
                      </div>
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-commercial-landing"
                          name="category"
                          value="commercial"
                          checked={formData.category === 'commercial'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-commercial-landing" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbBriefcase size={20} />
                          </span>
                          <span className="category-text">Commercial</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="quick-contact-form">
                    <div className="form-row-compact">
                      <div className="form-group">
                        <input
                          type="text"
                          name="name"
                          placeholder="Full Name *"
                          value={formData.name}
                          onChange={handleInputChange}
                          onFocus={() => handleFieldFocus('name')}
                          className="form-input-compact"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Phone Number *"
                          value={formData.phone}
                          onChange={handleInputChange}
                          onFocus={() => handleFieldFocus('phone')}
                          className="form-input-compact"
                          required
                        />
                      </div>
                    </div>
                    <div className="form-row-compact">
                      <div className="form-group">
                        <input
                          type="text"
                          name="pincode"
                          placeholder="Pin Code *"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          onFocus={() => handleFieldFocus('pincode')}
                          className="form-input-compact"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          name="city"
                          placeholder="City *"
                          value={formData.city}
                          onChange={handleInputChange}
                          onFocus={() => handleFieldFocus('city')}
                          className="form-input-compact"
                          required
                        />
                      </div>
                    </div>
                    {formData.category === 'residential' && (
                      <div className="form-group">
                        <select
                          name="monthlyBill"
                          value={formData.monthlyBill}
                          onChange={handleInputChange}
                          className="form-select-compact"
                          required
                        >
                          <option value="">Monthly Bill Range</option>
                          <option value="less-than-1500">Less than ₹1,500</option>
                          <option value="1500-2500">₹1,500 - ₹2,500</option>
                          <option value="2500-4000">₹2,500 - ₹4,000</option>
                          <option value="4000-8000">₹4,000 - ₹8,000</option>
                          <option value="more-than-8000">More than ₹8,000</option>
                        </select>
                      </div>
                    )}
                    {formData.category === 'commercial' && (
                      <>
                        <div className="form-group">
                          <input
                            type="text"
                            name="companyName"
                            placeholder="Company Name"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            onFocus={() => handleFieldFocus('companyName')}
                            className="form-input-compact"
                          />
                        </div>
                        <div className="form-group">
                          <select
                            name="monthlyBill"
                            value={formData.monthlyBill}
                            onChange={handleInputChange}
                            onFocus={() => handleFieldFocus('monthlyBill')}
                            className="form-select-compact"
                            required
                          >
                            <option value="">Monthly Bill Range</option>
                            <option value="0-50000">₹0 - ₹50,000</option>
                            <option value="50000-2lacs">₹50,000 - ₹2,00,000</option>
                            <option value="more-than-2lacs">More than ₹2,00,000</option>
                          </select>
                        </div>
                      </>
                    )}
                    <div className="form-group">
                      <input
                        type="email"
                        name="email"
                        placeholder="Email (Optional)"
                        value={formData.email}
                        onChange={handleInputChange}
                        onFocus={() => handleFieldFocus('email')}
                        className="form-input-compact"
                      />
                    </div>
                    <button type="submit" className="submit-btn-quick">
                      Get Free Quote
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Financing Options Section */}
        <section className="financing-section">
          <div className="container">
            <h2 className="financing-title">Flexible Financing Solutions</h2>
            <p className="financing-subtitle">We help arrange financing options that make solar affordable for everyone</p>
            <div className="financing-grid">
              <div className="financing-item">
                <div className="financing-icon">
                  <div className="icon-wrapper financing-nbfc">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <path d="M16 6L8 10V22L16 26L24 22V10L16 6Z" fill="#1F3A93" opacity="0.1"/>
                      <path d="M16 6L8 10V22L16 26L24 22V10L16 6Z" stroke="#1F3A93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 10L16 14L24 10M16 14V26" stroke="#1F3A93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="16" cy="10" r="2" fill="#1F3A93"/>
                    </svg>
                  </div>
                </div>
                <div className="financing-item-content">
                  <h3 className="financing-item-title">EMI from NBFCs</h3>
                  <p className="financing-item-text">
                    Easy monthly installments through leading NBFC partners. Quick approval and flexible repayment terms.
                  </p>
                </div>
              </div>

              <div className="financing-item">
                <div className="financing-icon">
                  <div className="icon-wrapper financing-bank">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <rect x="6" y="8" width="20" height="18" rx="2" fill="#1F3A93" opacity="0.1"/>
                      <rect x="6" y="8" width="20" height="18" rx="2" stroke="#1F3A93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 14H22M10 18H22M10 22H18" stroke="#1F3A93" strokeWidth="2.5" strokeLinecap="round"/>
                      <path d="M16 4L18 8H14L16 4Z" fill="#1F3A93"/>
                      <path d="M16 4L18 8H14L16 4Z" stroke="#1F3A93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="financing-item-content">
                  <h3 className="financing-item-title">Bank Loans</h3>
                  <p className="financing-item-text">
                    We facilitate loans from mainstream banks with competitive interest rates and favorable terms.
                  </p>
                </div>
              </div>

              <div className="financing-item">
                <div className="financing-icon">
                  <div className="icon-wrapper financing-project">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <path d="M16 4L4 12V24L16 28L28 24V12L16 4Z" fill="#1F3A93" opacity="0.1"/>
                      <path d="M16 4L4 12V24L16 28L28 24V12L16 4Z" stroke="#1F3A93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M4 12L16 18L28 12M16 18V28" stroke="#1F3A93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="16" cy="12" r="2.5" fill="#1F3A93"/>
                      <path d="M12 24L16 26L20 24" stroke="#1F3A93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="financing-item-content">
                  <h3 className="financing-item-title">Project Financing</h3>
                  <p className="financing-item-text">
                    Specialized financing solutions for large-scale commercial and utility projects.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Journey Section */}
        <section className="customer-journey-section">
          <div className="container">
            <div className="customer-journey-header">
              <h2 className="customer-journey-title">Your Solar Journey With Us</h2>
              <p className="customer-journey-subtitle">From your first call to generating clean energy, we walk you through every step with complete transparency</p>
            </div>
            
            <div className="customer-journey-steps">
              {/* Step 1 */}
              <div className="journey-step">
                <div className="journey-step-number">1</div>
                <div className="journey-step-content">
                  <div className="journey-step-icon">
                    <div className="icon-wrapper journey-contact">
                      <FaRegHandshake
                        className="journey-step-icon-symbol"
                        size={30}
                        aria-hidden="true"
                        focusable="false"
                        style={{ color: '#1D4ED8' }}
                      />
                    </div>
                  </div>
                  <h3 className="journey-step-title">You Reach Out to Us</h3>
                  <p className="journey-step-text">
                    Share your details through our contact form and we'll get in touch within 24 hours. No sales pressure, just genuine advice about what solar can do for your home or business.
                  </p>
                  <Link 
                    to="/callback" 
                    className="journey-contact-btn"
                    onClick={() => {
                      sessionStorage.setItem('homeScrollPosition', window.pageYOffset.toString());
                    }}
                  >
                    Schedule a Free Consultation
                  </Link>
                </div>
              </div>

              {/* Step 2 */}
              <div className="journey-step">
                <div className="journey-step-number">2</div>
                <div className="journey-step-content">
                  <div className="journey-step-icon">
                    <div className="icon-wrapper journey-survey">
                      <FaCompassDrafting
                        className="journey-step-icon-symbol"
                        size={30}
                        aria-hidden="true"
                        focusable="false"
                        style={{ color: '#B45309' }}
                      />
                    </div>
                  </div>
                  <h3 className="journey-step-title">Site Survey & Custom Design</h3>
                  <p className="journey-step-text">
                    Our expert visits your property at no cost to assess your roof, check for shadows, and create a digital design showing exactly how the solar system will look. You get to see everything before we install anything.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="journey-step">
                <div className="journey-step-number">3</div>
                <div className="journey-step-content">
                  <div className="journey-step-icon">
                    <div className="icon-wrapper journey-proposal">
                      <FaRegFileLines
                        className="journey-step-icon-symbol"
                        size={30}
                        aria-hidden="true"
                        focusable="false"
                        style={{ color: '#047857' }}
                      />
                    </div>
                  </div>
                  <h3 className="journey-step-title">Transparent Proposal & Pricing</h3>
                  <p className="journey-step-text">
                    You receive a detailed proposal with itemized costs, government subsidies you're eligible for, expected savings, and payback period. Everything is upfront with no surprises and no hidden fees.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="journey-step">
                <div className="journey-step-number">4</div>
                <div className="journey-step-content">
                  <div className="journey-step-icon">
                    <div className="icon-wrapper journey-paperwork">
                      <FaFileSignature
                        className="journey-step-icon-symbol"
                        size={30}
                        aria-hidden="true"
                        focusable="false"
                        style={{ color: '#C2410C' }}
                      />
                    </div>
                  </div>
                  <h3 className="journey-step-title">Paperwork & DISCOM Applications</h3>
                  <p className="journey-step-text">
                    We handle all the paperwork and DISCOM applications for you. From net metering applications to subsidy documentation, our team takes care of everything so you don't have to deal with the bureaucracy.
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="journey-step">
                <div className="journey-step-number">5</div>
                <div className="journey-step-content">
                  <div className="journey-step-icon">
                    <div className="icon-wrapper journey-installation">
                      <FaHelmetSafety
                        className="journey-step-icon-symbol"
                        size={30}
                        aria-hidden="true"
                        focusable="false"
                        style={{ color: '#6D28D9' }}
                      />
                    </div>
                  </div>
                  <h3 className="journey-step-title">Professional Installation</h3>
                  <p className="journey-step-text">
                    Our certified installers handle everything from mounting structures and panel installation to inverter setup and grid connection. Installation typically takes 1 to 2 days and is completed with minimal disruption to your routine.
                  </p>
                </div>
              </div>

              {/* Step 6 */}
              <div className="journey-step">
                <div className="journey-step-number">6</div>
                <div className="journey-step-content">
                  <div className="journey-step-icon">
                    <div className="icon-wrapper journey-activation">
                      <FaBolt
                        className="journey-step-icon-symbol"
                        size={30}
                        aria-hidden="true"
                        focusable="false"
                        style={{ color: '#DC2626' }}
                      />
                    </div>
                  </div>
                  <h3 className="journey-step-title">Activation & Handover</h3>
                  <p className="journey-step-text">
                    Once everything is tested and approved, we activate your system and hand over all documents including warranty cards, user manuals, monitoring app access, and maintenance schedule. You're all set to start saving on electricity bills.
                  </p>
                </div>
              </div>

              {/* Step 7 */}
              <div className="journey-step">
                <div className="journey-step-number">7</div>
                <div className="journey-step-content">
                  <div className="journey-step-icon">
                    <div className="icon-wrapper journey-support">
                      <FaLifeRing
                        className="journey-step-icon-symbol"
                        size={30}
                        aria-hidden="true"
                        focusable="false"
                        style={{ color: '#1D4ED8' }}
                      />
                    </div>
                  </div>
                  <h3 className="journey-step-title">Ongoing Support & Monitoring</h3>
                  <p className="journey-step-text">
                    We're here for you long after installation. With 1 year of free maintenance, regular health checks, and our support team just a call away, you can enjoy worry-free solar energy generation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Section for SEO */}
        <section className="blog-preview-section">
          <div className="container">
            <div className="blog-preview-header">
              <h2>Solar Insights & Guides</h2>
              <p>Stay informed with our latest solar energy insights, installation guides, and industry updates</p>
            </div>
            <div className="blog-preview-grid">
              <div className="blog-preview-card">
                <h3>Lunar Electric Solar Calculator</h3>
                <p>Free and accurate solar calculator for India. Calculate your solar potential, savings, and payback period with state-specific tariffs and government subsidies.</p>
                <a href="/solar-calculator" className="blog-preview-link">Use Solar Calculator →</a>
              </div>
              <div className="blog-preview-card">
                <h3>Rooftop Solar Installation Guide</h3>
                <p>Complete step-by-step guide to installing rooftop solar at home with government subsidies and compliance requirements.</p>
                <a href="/blogs/rooftop-solar-guide" className="blog-preview-link">Read Guide →</a>
              </div>
              <div className="blog-preview-card">
                <h3>Solar Panel Benefits for Homes</h3>
                <p>Discover the top 10 benefits of installing solar panels for Indian homes including cost savings and environmental benefits.</p>
                <a href="/blogs/solar-panel-benefits" className="blog-preview-link">Read Benefits →</a>
              </div>
            </div>
            <div className="blog-preview-cta">
              <a href="/blogs" className="btn-primary">View All Articles</a>
            </div>
          </div>
        </section>

        <About />
        <FAQs />
      </main>
    </>
  );
}
export default Main;









